import { inputAnatomy as parts } from '@chakra-ui/anatomy'
import type {
  PartsStyleFunction,
  PartsStyleObject,
  SystemStyleObject,
} from '@chakra-ui/theme-tools'
import { getColor, mode, transparentize } from '@chakra-ui/theme-tools'

const baseStyle: PartsStyleObject<typeof parts> = {
  field: {
    width: 'auto',
    minW: 0,
    maxW: '100%',
    outline: 0,
    position: 'relative',
    appearance: 'none',
    lineHeight: 'input',
    transitionProperty: 'common',
    transitionDuration: 'normal',
  },
}

const size: Record<string, SystemStyleObject> = {
  lg: {
    height: 'auto',
    px: 4,
    py: 3,
    borderRadius: 'md',
    fontSize: 'lg',
  },

  md: {
    height: 9,
    px: 3,
    py: 2,
    borderRadius: 'md',
    fontSize: {
      base: 'lg',
      lg: 'md',
    },
  },

  sm: {
    height: 'auto',
    px: 3,
    py: 1,
    borderRadius: 'md',
    fontSize: 'sm',
  },

  xs: {
    height: 'auto',
    px: 2,
    py: 1,
    borderRadius: 'sm',
    fontSize: 'xs',
  },
}

const sizes: Record<string, PartsStyleObject<typeof parts>> = {
  lg: {
    field: size.lg,
    addon: size.lg,
  },
  md: {
    field: size.md,
    addon: size.md,
  },
  sm: {
    field: size.sm,
    addon: size.sm,
  },
  xs: {
    field: size.xs,
    addon: size.xs,
  },
}

function getDefaults(props: Record<string, any>) {
  const { focusBorderColor: fc, errorBorderColor: ec } = props
  return {
    focusBorderColor: fc || mode('primary.500', 'primary.300')(props),
    errorBorderColor: ec || mode('red.500', 'red.300')(props),
  }
}

const variantOutline: PartsStyleFunction<typeof parts> = (props) => {
  const { theme } = props
  const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props)

  return {
    field: {
      border: '1px solid',
      borderColor: mode('gray.400', 'whiteAlpha.800')(props),
      bg: mode('gray.50', 'gray.900')(props),
      boxShadow: 'input',
      _hover: {
        bg: 'white',
        borderColor: mode('gray.500', 'whiteAlpha.400')(props),
      },
      _readOnly: {
        boxShadow: 'none !important',
        userSelect: 'all',
      },
      _disabled: {
        bg: mode('gray.200', 'gray.900')(props),
        color: mode('gray.600', 'gray.300')(props),
        cursor: 'not-allowed',
        opacity: 1,
      },
      _invalid: {
        borderColor: getColor(theme, ec),
        boxShadow: `0 0 0 2px ${transparentize(ec, 0.2)(theme)}`,
      },
      _focus: {
        bg: 'white',
        borderColor: getColor(theme, fc),
        boxShadow: `0 0 0 2px ${transparentize(fc, 0.2)(theme)}`,
        zIndex: 1,
      },
    },
    addon: {
      border: '1px solid',
      borderColor: mode('inherit', 'whiteAlpha.50')(props),
      bg: mode('gray.100', 'whiteAlpha.300')(props),
    },
  }
}

const variantFilled: PartsStyleFunction<typeof parts> = (props) => {
  const { theme } = props
  const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props)

  return {
    field: {
      border: '2px solid',
      borderColor: 'transparent',
      bg: mode('gray.100', 'whiteAlpha.50')(props),
      _hover: {
        bg: mode('gray.200', 'whiteAlpha.100')(props),
      },
      _readOnly: {
        boxShadow: 'none !important',
        userSelect: 'all',
      },
      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
      },
      _invalid: {
        borderColor: getColor(theme, ec),
      },
      _focus: {
        bg: 'transparent',
        borderColor: getColor(theme, fc),
      },
    },
    addon: {
      border: '2px solid',
      borderColor: 'transparent',
      bg: mode('gray.100', 'whiteAlpha.50')(props),
    },
  }
}

const variantFlushed: PartsStyleFunction<typeof parts> = (props) => {
  const { theme } = props
  const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props)

  return {
    field: {
      borderBottom: '1px solid',
      borderColor: 'inherit',
      borderRadius: 0,
      px: 0,
      bg: 'transparent',
      _readOnly: {
        boxShadow: 'none !important',
        userSelect: 'all',
      },
      _invalid: {
        borderColor: getColor(theme, ec),
        boxShadow: `0px 1px 0px 0px ${getColor(theme, ec)}`,
      },
      _focus: {
        borderColor: getColor(theme, fc),
        boxShadow: `0px 1px 0px 0px ${getColor(theme, fc)}`,
      },
    },
    addon: {
      borderBottom: '2px solid',
      borderColor: 'inherit',
      borderRadius: 0,
      px: 0,
      bg: 'transparent',
    },
  }
}

const variantUnstyled: PartsStyleObject<typeof parts> = {
  field: {
    bg: 'transparent',
    px: 0,
    height: 'auto',
  },
  addon: {
    bg: 'transparent',
    px: 0,
    height: 'auto',
  },
}

const variants = {
  outline: variantOutline,
  filled: variantFilled,
  flushed: variantFlushed,
  unstyled: variantUnstyled,
}

const defaultProps = {
  size: 'md',
  variant: 'outline',
}

export const Input = {
  parts: parts.keys,
  baseStyle,
  sizes,
  variants,
  defaultProps,
}
